<template>
  <fragment v-if="renderType === type.UploadDataTable">
    <div class="container">
      <div class="row justify-content-center">
        <div class="col-lg-10 col-xl-8">

          <div class="card shadow-sm">
            <div class="card-body">

              <!-- Helper text -->
              <div
                  class="alert alert-info py-2 mb-4"
                  v-if="fileObj.type === 'utilityBill_1' || fileObj.type === 'utilityBill_2'"
              >
                <div>
                  Upload two utility bill documents (e.g., electricity, water, gas).<br>
                  Please upload them one by one and select the correct type:
                  <strong>Utility Bill (A)</strong> and <strong>Utility Bill (B)</strong>.
                </div>
              </div>

              <!-- Step 1: File -->
              <div class="form-group row align-items-center mb-3">
                <label class="col-sm-3 col-form-label font-weight-bold">
                  1) Select File
                </label>
                <div class="col-sm-9">
                  <app-input
                      type="file"
                      v-model="fileObj.files"
                      placeholder="Choose file"
                  />
                  <small class="text-muted d-block mt-3">
                    Select the document first. Then choose type and set a name if needed.
                  </small>
                </div>
              </div>

              <!-- Step 2: Type -->
              <div class="form-group row align-items-center mb-3">
                <label class="col-sm-3 col-form-label font-weight-bold">
                  2) Document Type
                </label>
                <div class="col-sm-9">
                  <app-input
                      type="select"
                      :list="list"
                      v-model="fileObj.type"
                      placeholder="Select type"
                  />
                </div>
              </div>

              <!-- Step 3: Name -->
              <div class="form-group row align-items-center mb-2">
                <label class="col-sm-3 col-form-label font-weight-bold">
                  3) File Name
                  <small class="text-muted font-weight-normal d-block">(optional)</small>
                </label>
                <div class="col-sm-9">
                  <app-input
                      type="text"
                      v-model="fileObj.file_name"
                      placeholder="e.g., Electricity Bill - Jan 2026"
                  />
                </div>
              </div>

              <!-- Actions -->
              <div class="border-top pt-3 mt-4">
                <div class="d-flex align-items-center justify-content-end flex-wrap">
                  <el-button
                      class="btn btn-primary el-button--default"
                      @click="uploadBtnHandler"
                      :disabled="isUploadDisabled"
                  >
                    Upload File
                  </el-button>
                </div>

                <div class="text-muted mt-2" style="font-size: 12px;">
                  Tip: Upload after confirming type and name.
                </div>
              </div>

            </div>
          </div>

          <!-- Guide / Accordion -->
          <div class="mt-4">
            <div class="d-flex align-items-center flex-wrap mb-3">
              <h6 class="mb-0 mr-2 font-weight-bold">Upload these documents:</h6>
              <button type="button"
                      @click="showGuide = !showGuide" 
                      class="btn btn-sm btn-primary ml-2 px-3 shadow-sm"
                      style="border-radius: 20px; font-weight: 600; font-size: 12px;">
                {{ showGuide ? 'Hide Guidance' : 'Need Help? Click Here' }}
              </button>
            </div>
            <div id="documentUploadGuide" class="accordion" v-if="showGuide">
              <div class="card mb-2" v-for="(section, sIdx) in sections" :key="sIdx">
                <div class="card-header p-0" :id="'heading-'+sIdx">
                  <h5 class="mb-0">
                    <button
                        class="accordion-toggle btn btn-block d-flex justify-content-between align-items-center collapsed p-3"
                        type="button"
                        :data-toggle="'collapse'"
                        :data-target="'#collapse-'+sIdx"
                        aria-expanded="false"
                        :aria-controls="'collapse-'+sIdx"
                    >
                      <div class="d-flex align-items-start">
                        <span class="accordion-img mr-3" aria-hidden="true"></span>
                        <div class="flex-grow-1">
                          <div class="accordion-title">{{ section.title }}</div>
                          <div class="accordion-prompt">{{ section.prompt }}</div>
                        </div>
                      </div>
                      <span class="chev">&#9662;</span>
                    </button>
                  </h5>
                </div>

                <div
                    :id="'collapse-'+sIdx"
                    class="collapse"
                    :aria-labelledby="'heading-'+sIdx"
                    data-parent="#documentUploadGuide"
                >
                  <div class="card-body">
                    <ul class="mb-0">
                      <li v-for="(line, lIdx) in section.content" :key="sIdx+'-'+lIdx">
                        {{ line }}
                      </li>
                    </ul>
                  </div>
                </div>

              </div>
            </div>
            <el-tooltip
                effect="dark"
                placement="top"
                popper-class="assign-tooltip"
            >
              <div slot="content" style="max-width: 320px; line-height: 1.5;">
                <strong>Assign uploaded files to checklist</strong>
                <br><br>
                By clicking this button, the uploaded files will be assigned to their
                respective screening checklist items.
                <br><br>
                You can then arrange the checklist and manage attached files from the
                <strong>Input Details</strong> section above (via the tabs).
              </div>

              <el-button
                  class="btn btn-success el-button--default mr-2 mt-4"
                  @click="assignUploadedFilesToChecklist"
                  v-if="isAssignDisabled"
                  style="font-weight: 600; padding: 12px 24px; font-size: 15px; color: white; background-color: #28a745; border-color: #28a745;"
              >
                <i class="fa fa-check-circle mr-2"></i>
                Assign uploaded files to Screening Checklist
              </el-button>
            </el-tooltip>

          </div>

        </div>
      </div>
    </div>
  </fragment>

  <!-- keep your other fragments as-is -->
  <fragment v-else-if="renderType === type.CandidateDetail">
    <div class="form-group col-sm-4">
      <label>Upload File</label>
      <el-button class="btn btn-primary el-button--default col-sm-12 uploadBtnCustomStyle"
                 @click.prevent="$refs.file.click()">
        Choose&nbsp;File
      </el-button>
      <input type="file"
             ref="file"
             style="display:none"
             v-on:change="fileChange"/>
    </div>
  </fragment>

  <fragment v-else-if="renderType === type.Search">
    <div class="form-group col-sm-4">
      <label>Upload File</label>
      <el-button class="btn btn-primary el-button--default col-sm-12 uploadBtnCustomStyle"
                 @click.prevent="$refs.file.click()">
        Choose&nbsp;File
      </el-button>
      <input type="file"
             ref="file"
             style="display:none"
             v-on:change="fileChange"/>
    </div>
  </fragment>

  <fragment v-else>
    <div class="form-group col-sm-4">
      <label>Upload Result (PDF)</label>
      <el-button class="btn btn-primary el-button--default col-sm-12"
                 @click.prevent="$refs.file.click()"
                 style="background-color:#4466f2; color: #ffffff">
        Choose&nbsp;File
      </el-button>
      <input type="file"
             ref="file"
             style="display:none"
             accept="application/pdf"
             v-on:change="fileChange"/>
    </div>
  </fragment>
</template>



<script>
import {documentTypeList} from '@Views/Screening/utility/constant';
import {File} from "@Views/Screening/utility/apiAgent";
import {uploadBtnRenderType} from '@Views/Screening/utility/enums';

export default {
  name: "UploadBtn",
  props: {
    fileObj: {
      type: Object,
    },
    emailAddress: {
      type: String,
    },
    tableId: {
      type: String
    },
    renderType: {
      type: String
    },
    searchDocument: {
      type: Object
    },
    filesForDocumentTable: {
      type: Array
    },
    error: {
      type: Object
    },
    screeningSchedule: {
      type: Object
    },
    metaData: {
      type: Object
    },
    autoUpload: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      showGuide: false,
      list: documentTypeList,
      searchUploadedFile: null,
      type: uploadBtnRenderType
      ,
      pendingPreview: null,
      pendingFileName: null,
      uploadedPath: null,
      uploadedName: null,
      sections: [
        {
          title: 'Proof of Identity (Passport / Driving Licence)',
          prompt: 'Upload a valid photo ID.',
          content: [
            'Passport (photo page) or Driving Licence (front & back)',
            'Must show name, date of birth, and clear photo',
            'All corners of the document must be visible'
          ]
        },
        {
          title: 'Right-to-Work / Visa',
          prompt: 'Upload your right-to-work proof.',
          content: [
            'Visa, residence permit, or share-code evidence',
            'Must show permission to work in the UK',
            'Document must be in date and fully readable'
          ]
        },
        {
          title: 'Proof of Address — Document A',
          prompt: 'Upload address proof (Doc A).',
          content: [
            'Utility bill, bank statement, or official letter',
            'Must be dated within the last 3 months',
            'Must show your full name and current address',
            'Mobile phone bills not accepted'
          ]
        },
        {
          title: 'Proof of Address — Document B',
          prompt: 'Upload address proof (Doc B). Different from Doc A.',
          content: [
            'A second, different address document (utility, bank, council tax, etc.)',
            'Must also be within the last 3 months',
            'Must show name and address clearly'
          ]
        },
        {
          title: 'Bank Statement',
          prompt: 'Upload recent bank statement.',
          content: [
            'Must be dated within the last 3 months',
            'Must show your name, address, bank logo, and issue date',
            'Online statements accepted if complete'
          ]
        },
        {
          title: 'SIA Licence (If held)',
          prompt: 'Upload your SIA licence.',
          content: [
            'Front and back of SIA card or screenshot from SIA portal',
            'Ensure licence number and expiry are visible'
          ]
        },
        {
          title: 'ACT Certificate',
          prompt: 'Upload your ACT Certificate.',
          content: [
            'Certificate from the ACT portal or a clear photo of the physical copy',
            'Must show your name and the date of the certificate'
          ]
        },
        {
          title: 'File Upload Instructions',
          prompt: 'Upload clear, readable files.',
          content: [
            'Accepted formats: PDF, JPG, PNG',
            'Ensure all text is readable and nothing is cut off',
            'Blurry documents may be rejected'
          ]
        }
      ]
    }
  },
  computed: {
    isUploadDisabled() {
      return this.screeningSchedule?.id && this.isAdmin;
    },
    isAssignDisabled() {
      const s = this.screeningSchedule || {}
      return this.$store.state.user.loggedInUser.id > 0 && !!s.id && !!s.applicant_id
    }
  },
  methods: {

    assignUploadedFilesToChecklist() {
      File.attachDocumentToChecklist(this.screeningSchedule)
          .then(({ data }) => {
            this.$toastr.s(data.message);

            // reload page after success
            setTimeout(() => {
              window.location.reload();
            }, 1000); // small delay so user can see the toast
          })
          .catch((err) => {
            const res = err && err.response && err.response.data ? err.response.data : null;

            if (res && res.errors) {
              const msgs = [];
              Object.values(res.errors).forEach(v => {
                if (Array.isArray(v)) msgs.push(...v);
              });

              this.$toastr.e(msgs.length ? msgs.join(' ') : (res.message || 'Something went wrong.'));
            } else {
              this.$toastr.e((res && res.message) || 'Something went wrong.');
            }
          });
    },
    resetUploadForm() {

      // reset file input (important)
      if (this.$refs.fileInput) {
        this.$refs.fileInput.value = null;
      }

      // reset fileObj safely
      this.fileObj = {
        ...this.fileObj,
        file_name: '',
        documentId: null,
        files: [],
        type: null
      };
    },
    uploadBtnHandler() {
      let data;

      if (this.renderType === this.type.UploadDataTable) {
        data = {
          ...this.fileObj,
          email: this.emailAddress,
          metaData: this.metaData ?? {},
        };
      } else if (this.renderType === this.type.CandidateDetail) {
        data = {
          files: this.searchUploadedFile,
          type: 'CandidateDetailDocument',
          email: this.emailAddress,
          metaData: this.metaData ?? {},
        };
      } else {
        data = {
          files: this.searchUploadedFile,
          type: this.fileObj.search_type,
          email: this.emailAddress,
          metaData: this.metaData ?? {},
        };
      }

      const formData = new FormData();

      Object.entries(data).forEach(([key, value]) => {
        if (key === 'metaData') {
          if (value && typeof value === 'object' && !Array.isArray(value)) {
            Object.entries(value).forEach(([metaKey, metaValue]) => {
              if (metaValue !== null && metaValue !== undefined && metaValue !== '') {
                formData.append(`metaData[${metaKey}]`, metaValue);
              }
            });
          }
          return;
        }

        if (key === 'files') {
          if (Array.isArray(value)) {
            value.forEach(file => {
              if (file) formData.append('files[]', file);
            });
          } else if (value) {
            formData.append('files[]', value);
          }
          return;
        }

        if (value !== null && value !== undefined && value !== '') {
          formData.append(key, value);
        }
      });

      const uploadPromise =
          (this.renderType === this.type.UploadDataTable && this.fileObj.documentId)
              ? File.updateDocument(this.fileObj.documentId, formData)
              : File.uploadDocument(formData);

      uploadPromise
          .then(({ data }) => {
            if (this.renderType === this.type.Search) {
              const _documentId = data.data[0].id;
              this.$set(this.fileObj.files, this.fileObj.files.length, _documentId);
              this.$set(this.searchDocument, 'file', {
                id: _documentId,
                name: data.data[0].file_name,
                fileURL: window.URL.createObjectURL(this.searchUploadedFile),
                ...data.data[0]
              });
              this.searchUploadedFile = null;
            } else {
              this.fileObj.documentId = data.data[0].id;
            }

            this.$toastr.s(data.message);
            this.resetUploadForm();

            // 🔹 Notify parent about the new document
            if (data.data && data.data[0]) {
              this.$hub.$emit('document-uploaded', data.data[0]);
            }

            // 🔹 Reload the document table immediately after upload
            if (this.tableId) {
              this.$hub.$emit(`reload-${this.tableId}`);
            }
          })
          .catch((err) => {
            const res = err?.response?.data || null;

            if (res && res.errors) {
              const msgs = [];
              Object.values(res.errors).forEach(v => {
                if (Array.isArray(v)) msgs.push(...v);
              });
              this.$toastr.e(msgs.length ? msgs.join(' ') : (res.message || 'Something went wrong.'));
            } else {
              this.$toastr.e((res && res.message) || 'Something went wrong.');
            }
          });
    },
    fileChange(event) {
      this.isError = false;

      const files = event.target.files || event.dataTransfer.files;

      if (!files || !files.length) {
        return;
      }

      const isExist = this.filesForDocumentTable &&
          this.filesForDocumentTable.find(x => x.name === files[0].name);

      if (isExist) {
        this.$set(this.error, 'errorMessage', 'File with this name already inserted.');
        this.$set(this.error, 'isError', true);
        return;
      }

      this.searchUploadedFile = files[0];

      try {
        if (
            this.searchUploadedFile &&
            this.searchUploadedFile.type &&
            this.searchUploadedFile.type.startsWith('image/')
        ) {
          this.pendingPreview = window.URL.createObjectURL(this.searchUploadedFile);
        } else {
          this.pendingPreview = null;
        }

        this.pendingFileName = this.searchUploadedFile.name;

      } catch (e) {
        this.pendingPreview = null;
      }

      this.$emit('selected', this.searchUploadedFile);

      // 🔹 Auto upload immediately after selecting file if enabled
      if (this.autoUpload) {
        this.$nextTick(() => {
          this.uploadBtnHandler();
        });
      }
    },
    isImage(fileName) {
      if (!fileName) return false;
      const ext = fileName.toString().split('.').pop().toLowerCase();
      return ['jpg', 'jpeg', 'png', 'gif', 'svg'].includes(ext);
    },
    getFileIcon(fileName) {
      if (!fileName) return 'file';
      const ext = fileName.toString().split('.').pop().toLowerCase();
      if (ext === 'pdf') return 'file-text';
      return 'file';
    },
    getFileUrl(path) {
      if (!path) return '';
      const p = path.toString();
      if (p.startsWith('blob:') || p.startsWith('http')) return p;
      const normalized = p.startsWith('/') ? p : `/${p}`;
      return `${window.location.origin}${normalized}`;
    },
    viewFile(path) {
      const url = this.getFileUrl(path);
      if (url) window.open(url, '_blank');
    }
  }
}
</script>

<style scoped>
.uploadBtnCustomStyle {
  background-color: #4466f2;
  color: #ffffff;
}

.document-preview-container {
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  margin-top: 10px;
  justify-content: flex-start;
  padding-left: 15px;
  padding-right: 15px;
}
.document-preview-card {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  width: auto;
  animation: fadeIn 0.4s ease;
}
.file-thumbnail-box {
  width: 140px;
  height: 100px;
  background: #ffffff;
  border: 1px solid #e0e0e0;
  border-radius: 10px;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  transition: transform 0.2s ease, box-shadow 0.2s ease;
  cursor: pointer;
}
.file-thumbnail-box:hover {
  transform: translateY(-2px);
  box-shadow: 0 6px 16px rgba(0, 0, 0, 0.1);
}
.file-thumbnail-box img {
  width: 100%;
  height: 100%;
  object-fit: contain;
  padding: 5px;
}
.file-icon-large {
  color: #6c757d;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}
.file-name-text {
  font-weight: 600;
  margin-top: 5px;
  font-size: 14px;
  color: #3498db;
  max-width: 100%;
  text-align: left;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: color 0.2s;
}
.document-preview-card:hover .file-name-text {
  color: #2980b9;
  text-decoration: underline;
}
.upload-status-badge {
  color: #27ae60;
  font-size: 13px;
  display: flex;
  align-items: center;
  gap: 4px;
  margin-top: 6px;
  font-weight: 600;
}
@keyframes fadeIn {
  from { opacity: 0; transform: scale(0.95); }
  to { opacity: 1; transform: scale(1); }
}

.tag {
  display: inline-block;
  background: #f0f0f0;
  color: #333;
  padding: 5px 10px;
  margin: 3px;
  border-radius: 5px;
  font-size: 14px;
}

/* Accordion and list font sizing */
#documentUploadGuide .card-header .btn {
  font-size: 14px;
}

#documentUploadGuide .card-body ul li {
  font-size: 14px;
  list-style: disc;
  margin-left: 18px;
}

/* Remove underline and make chevron on right */
#documentUploadGuide .accordion-toggle {
  text-decoration: none !important;
  color: #333 !important;
  background: transparent !important;
  border: 0 !important;
  padding: 0.5rem 0.75rem !important;
  text-align: left;
}

#documentUploadGuide .accordion-title {
  font-size: 12px;
  font-weight: 700;
}

#documentUploadGuide .accordion-prompt {
  font-size: 12px;
  color: #666;
  margin-top: 2px;
}

/* hide the placeholder image so text starts flush */
#documentUploadGuide .accordion-img {
  display: none !important;
}

#documentUploadGuide .card-body ul li {
  font-size: 12px;
}

#documentUploadGuide .chev {
  font-size: 12px;
}

#documentUploadGuide .chev {
  font-size: 14px;
  transition: transform 0.18s ease;
  transform-origin: center;
  color: #666;
}

/* rotate chevron when expanded (bootstrap removes 'collapsed' when open) */
#documentUploadGuide .accordion-toggle:not(.collapsed) .chev {
  transform: rotate(180deg);
}
</style>
